"use client";

import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import { parseSpecialGuestsForBadges } from "@workspace/types";
import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { Label } from "@workspace/ui/components/label";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo, useMemo } from "react";
import { Facepile } from "@/components/shared/facepile";
import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { SetSocialButtons } from "@/components/shared/set-social-buttons";
import { SocialButtonGroup } from "@/components/shared/social-button-group";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import type { ShowArtist } from "@/lib/types/show-types";

interface ShowHeadlinerCardProps {
	showArtist: ShowArtist;
	className?: string;
	specialGuests?: string | null;
	socialInteractions?: Array<{
		id: string;
		username: string | null;
		display_name: string;
		manual_avatar_url?: string | null;
		google_avatar_url?: string | null;
	}>;
	showSocialLinks?: {
		youtube?: string | null;
		soundcloud?: string | null;
		appleMusic?: string | null;
		oneThousandOneTracklists?: string | null;
		setlistFm?: string | null;
	};
}

export const ShowHeadlinerCard = memo<ShowHeadlinerCardProps>(
	function ShowHeadlinerCard({ showArtist, className, specialGuests, socialInteractions, showSocialLinks }) {
		const preferences = useUserPreferences();
		const { artist, time_start, time_end } = showArtist;

		const timeRange = useMemo(() => {
			if (!time_start) return null;

			try {
				// time_start and time_end are now full timestamps from database
				return formatTimeRangeWithPreferences(
					time_start,
					time_end || undefined,
					preferences,
				);
			} catch {
				return null;
			}
		}, [time_start, time_end, preferences]);

		const genres = useMemo(() => {
			if (!artist?.spotify_genres && !artist?.manual_genres) return [];

			const spotifyGenres = Array.isArray(artist.spotify_genres)
				? artist.spotify_genres
				: [];
			const manualGenres = Array.isArray(artist.manual_genres)
				? artist.manual_genres
				: [];

			const allGenres = [...manualGenres, ...spotifyGenres];
			return Array.from(new Set(allGenres));
		}, [artist?.spotify_genres, artist?.manual_genres]);

		if (!artist) {
			return null;
		}

		const hasGenres = Boolean(genres.length > 0);
		const hasArtistSocials = Boolean(
			artist.spotify_url ||
			artist.youtube_url ||
			artist.instagram_url ||
			artist.twitter_url ||
			artist.website_url
		);
		const parsedSpecialGuests = specialGuests ? parseSpecialGuestsForBadges(specialGuests) : [];
		const hasSpecialGuests = parsedSpecialGuests.length > 0;
		const hasFollowing = Boolean(
			socialInteractions && socialInteractions.length > 0,
		);
		const hasSetLinks = showSocialLinks && (
			showSocialLinks.youtube ||
			showSocialLinks.soundcloud ||
			showSocialLinks.appleMusic ||
			showSocialLinks.oneThousandOneTracklists ||
			showSocialLinks.setlistFm
		);

		return (
			<Card className={cn("w-full border bg-card", className)}>
				<CardHeader className="p-3">
					<div className="flex gap-3">
						<ClickableArtistPhoto
							href={`/artists/${createArtistSlug(artist.name, artist.slug)}`}
							name={artist.name}
							imageUrl={artist.image_url}
							size="lg"
							rounded="card"
							trackingData={{
								artistId: artist.id,
								artistName: artist.name,
								source: "discover",
							}}
						/>

						<div className="min-w-0 flex-1 flex flex-col gap-1">
							<h3 className="font-semibold text-lg leading-tight">
								{artist.name}
							</h3>

							{(timeRange || !time_start) && (
								<div className="text-muted-foreground text-xs">
									{timeRange || "Set Time: TBA"}
								</div>
							)}

							{/* Row 1: Genres + Socials - inline with artist info */}
							{(hasGenres || hasArtistSocials) && (() => {
								// If only one item exists, show single column (left-aligned)
								if (!hasGenres || !hasArtistSocials) {
									return (
										<div className="space-y-1 mt-1">
											{hasGenres && (
												<>
													<Label>Genres</Label>
													<GenreBadgeListFromData
														spotifyGenres={artist.spotify_genres || null}
														manualGenres={artist.manual_genres || null}
														maxDisplay={3}
														badgeClassName="h-5 px-2 text-xs"
													/>
												</>
											)}
											{hasArtistSocials && (
												<>
													<Label>Socials</Label>
													<SocialButtonGroup
														name={artist.name}
														links={{
															spotify: artist.spotify_url || null,
															youtube: artist.youtube_url || null,
															instagram: artist.instagram_url || null,
															twitter: artist.twitter_url || null,
															website: artist.website_url || null,
														}}
														size="xs"
													/>
												</>
											)}
										</div>
									);
								}

								// Both items exist - show two columns
								return (
									<div className="grid grid-cols-2 gap-4 mt-1">
										<div className="space-y-1">
											<Label>Genres</Label>
											<GenreBadgeListFromData
												spotifyGenres={artist.spotify_genres || null}
												manualGenres={artist.manual_genres || null}
												maxDisplay={3}
												badgeClassName="h-5 px-2 text-xs"
											/>
										</div>
										<div className="space-y-1">
											<Label>Socials</Label>
											<SocialButtonGroup
												name={artist.name}
												links={{
													spotify: artist.spotify_url || null,
													youtube: artist.youtube_url || null,
													instagram: artist.instagram_url || null,
													twitter: artist.twitter_url || null,
													website: artist.website_url || null,
												}}
												size="xs"
											/>
										</div>
									</div>
								);
							})()}
						</div>
					</div>
				</CardHeader>

				{(hasSpecialGuests || hasFollowing || hasSetLinks) && (
					<CardContent className="px-3 pt-0 pb-3">
						{/* Aligned with artist info (after photo) */}
						<div className="flex gap-3">
							{/* Spacer to align with photo width (64px = w-16) */}
							<div className="w-16 flex-shrink-0" />
							<div className="flex-1 flex flex-col gap-y-3">

								{/* Set Social Links (setlist.fm, etc.) */}
								{hasSetLinks && showSocialLinks && (
									<SetSocialButtons
										setName={`${artist.name} set`}
										links={showSocialLinks}
										size="xs"
									/>
								)}

								{/* Row 2: Special Guests (left-aligned) */}
								{hasSpecialGuests && (
									<div className="space-y-1">
										<Label>Special Guests</Label>
										<div className="flex flex-wrap gap-1">
											{parsedSpecialGuests.map((guest, index) => (
												<Link
													key={index}
													href={`/artists/${createArtistSlug(guest)}`}
													className="cursor-pointer rounded-md bg-primary/10 px-2 py-1 font-medium text-primary text-xs transition-colors duration-200 hover:bg-primary/20"
												>
													{guest}
												</Link>
											))}
										</div>
									</div>
								)}

								{/* Row 3: People you follow (full width) */}
								{hasFollowing && (
									<div className="space-y-1">
										<Label>People you follow</Label>
										<div>
											<Facepile
												interactions={socialInteractions || []}
												size="sm"
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				)}

				{Boolean(artist.bio) && (
					<CardContent className="px-3 pt-0 pb-3">
						<p className="text-muted-foreground text-sm leading-relaxed">
							{artist.bio as string}
						</p>
					</CardContent>
				)}
			</Card>
		);
	},
);
